import React, { useState } from "react";
import {
  Control,
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormClearErrors,
  Controller,
} from "react-hook-form";
import { Col, Row, Form } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { FaAnglesDown, FaAnglesUp } from "react-icons/fa6";
import { ValoresDespesaParceladaResponse } from "types/request/contasPagar/ValoresDespesaParceladaResponse";
import { CustomDatePicker } from "shared/components/date/CustomDatePicker";
import { MesCompetencia } from "shared/components/date/MesCompetencia";
import CustomNumberInput from "shared/components/input/CustomNumberInput";
import TextInput from "shared/components/input/TextInput";
import SelectAsync from "shared/components/select/SelectAsync";
import CirculoSinalNegativo from "shared/components/circuloSinalNegativo/CirculoSinalNegatvo";
import CustomSwitch from "shared/components/Switch/CustomSwitch";

import {
  criarDataByLocalDate,
  formatarValorMonetario,
  formatarValorMonetarioInputNumerico,
  valoresDiferentes,
} from "shared/util/functions/formatacoesDeValores/FormatarValores";
import showAlert from "shared/util/functions/alert/showAlert";
import { criarSelectOptions } from "shared/util/functions/selectOptions/SelectOptionsFunctions";
import { CadastrarDespesaParceladaGroupPayForm } from "types/reactHookForm/formTypes/CadastrarDespesaParceladaGroupPayForm";
import InputAnexo from "shared/components/input/InputAnexo";
import CamposPagamentoParcela from "./CamposPagamentoParcela";
import { SingleValue } from "react-select";
import { SelectOptionType } from "types/select/SelectOptionType";
import { ClassePlanoContasYupSchemaDTO } from "types/dto/contasPagar/ClassePlanoContasYupSchemaDTO";
import { RegistroFornecedorSelectAsyncDTO } from "types/dto/fornecedor/RegistroFornecedorSelectAsyncDTO";

interface SecaoDadosDespesaModalCadastrarDespesaParceladaGroupPayProps {
  parcelas: FieldArrayWithId<CadastrarDespesaParceladaGroupPayForm, "parcelas">[];
  adicionarParcela: UseFieldArrayAppend<CadastrarDespesaParceladaGroupPayForm, "parcelas">;
  removerParcela: UseFieldArrayRemove;
  valorTotalParcelas: number;

  classesConta: FieldArrayWithId<CadastrarDespesaParceladaGroupPayForm, "classesConta">[];
  adicionarClasseConta: UseFieldArrayAppend<CadastrarDespesaParceladaGroupPayForm, "classesConta">;
  removerClasseConta: UseFieldArrayRemove;
  possuiContaProvisionamento: boolean;
  valorTotalClassesConta: number;

  control: Control<CadastrarDespesaParceladaGroupPayForm>;
  register: UseFormRegister<CadastrarDespesaParceladaGroupPayForm>;
  setValue: UseFormSetValue<CadastrarDespesaParceladaGroupPayForm>;
  watch: UseFormWatch<CadastrarDespesaParceladaGroupPayForm>;
  clearErrors: UseFormClearErrors<CadastrarDespesaParceladaGroupPayForm>;

  valorTotalInicial: number;
  condominioId: number;
}

const SecaoDadosDespesaModalCadastrarDespesaParceladaGroupPay: React.FC<
  SecaoDadosDespesaModalCadastrarDespesaParceladaGroupPayProps
> = (props) => {
  const [expandirParcelas, setExpandirParcelas] = useState<boolean>(false);
  const [informarManualmente, setInformarManualmente] = useState<{ [index: number]: boolean }>({});
  const [parcelaUiState, setParcelaUiState] = useState<{ [index: number]: { showAttachment: boolean } }>({});

  const handleParcelaUiStateChange = (index: number, key: "showAttachment", value: boolean) => {
    setParcelaUiState((prev) => ({ ...prev, [index]: { ...(prev[index] || {}), [key]: value } }));

    if (key === "showAttachment" && !value) {
      props.setValue(`parcelas.${index}.anexos`, null, { shouldValidate: true });
    }
  };

  const handleClasseContaChange = (
    opcaoSelecionada: SingleValue<SelectOptionType<ClassePlanoContasYupSchemaDTO>>,
    index: number
  ) => {
    props.clearErrors(`classesConta.${index}.classePlano`);

    if (opcaoSelecionada?.value.idContaProvisionamentoVinculada) {
      props.setValue(
        `classesConta.${index}.contaProvisionamento`,
        criarSelectOptions(
          opcaoSelecionada.value.idContaProvisionamentoVinculada,
          opcaoSelecionada.value.nomeContaProvisionamentoVinculada
        )
      );
      props.clearErrors(`classesConta.${index}.contaProvisionamento`);
    }
  };

  const handleFornecedorChange = (
    opcaoSelecionada: SingleValue<SelectOptionType<RegistroFornecedorSelectAsyncDTO>>
  ) => {
    props.clearErrors("fornecedor");

    if (opcaoSelecionada?.value?.detalhesClassePlanoContas?.idClassePlano) {
      props.setValue(
        "classesConta.0.classePlano",
        criarSelectOptions(
          { idClassePlano: opcaoSelecionada.value.detalhesClassePlanoContas.idClassePlano },
          opcaoSelecionada.value.detalhesClassePlanoContas.classePlanoContasNome
        )
      );
      props.setValue("classesConta.0.descricao", opcaoSelecionada.value.descricaoClassePlanoFornecedor);
      props.clearErrors("classesConta.0.classePlano");

      if (opcaoSelecionada.value.detalhesClassePlanoContas.idContaProvisionamentoVinculada) {
        props.setValue(
          "classesConta.0.contaProvisionamento",
          criarSelectOptions(
            opcaoSelecionada.value.detalhesClassePlanoContas.idContaProvisionamentoVinculada,
            opcaoSelecionada.value.detalhesClassePlanoContas.nomeContaProvisionamentoVinculada
          )
        );
        props.clearErrors("classesConta.0.contaProvisionamento");
      }
    } else {
      props.setValue("classesConta.0.classePlano", null);
      props.setValue("classesConta.0.descricao", null);
      props.setValue("classesConta.0.contaProvisionamento", null);
      props.clearErrors("classesConta.0.classePlano");
    }

    if (opcaoSelecionada?.value?.naturezaRendimento) {
      props.setValue(
        "naturezaRendimento",
        criarSelectOptions(
          opcaoSelecionada.value.naturezaRendimento.id,
          opcaoSelecionada.value.naturezaRendimento.codigoDescricao
        )
      );
    } else {
      props.setValue("naturezaRendimento", null);
    }
  };

  const distribuirValorTotalNasParcelas = (valorTotal: number | undefined) => {
    if (valorTotal && props.parcelas.length > 0) {
      const numeroParcelas = props.parcelas.length;
      const valorParcela = parseFloat((valorTotal / numeroParcelas).toFixed(2));
      const diferenca = valorTotal - valorParcela * numeroParcelas;

      props.parcelas.forEach((_, index) => {
        const valor = index === 0 ? valorParcela + diferenca : valorParcela;
        props.setValue(`parcelas.${index}.valorParcela`, valor, { shouldValidate: true });
        props.setValue(`parcelas.${index}.valorLiquido`, valor, { shouldValidate: true });
      });
    }
  };

  const criarParcelaVazia = (): ValoresDespesaParceladaResponse => ({
    valorLiquido: undefined,
    valorParcela: undefined,
    dataVencimento: null,
    mesCompetencia: null,
    conta: undefined,
    formaPagamento: undefined,
    codigoDeBarras: "",
    anexos: null,
    tipoFormaPagamentoPix: null,
    codigoCopiaColaPix: null,
  });

  const renderCampoValorTotal = () => (
    <Row>
      <Col xs={12} sm={2}>
        <CustomNumberInput
          id="valor-total-despesa-parcelada"
          name="valorTotal"
          label="Valor total (R$) *"
          prefix="R$ "
          control={props.control}
          register={props.register}
          setValue={props.setValue}
          fixedDecimalScale={true}
          decimalScale={2}
          defaultValue={props.valorTotalInicial}
          placeholder="R$ 0,00"
          usarValidacaoParaFormulario
          onBlur={(e) => {
            const valor = formatarValorMonetarioInputNumerico(e.target.value);
            distribuirValorTotalNasParcelas(valor || 0);
          }}
          isAllowed={(valor) => {
            const { floatValue } = valor;
            return floatValue === undefined || (floatValue >= 0 && floatValue <= 999999999999.99);
          }}
        />
      </Col>
    </Row>
  );

  const renderParcelas = () => {
    return (
      <div className="mt-4">
        <span className="span-parcelas fw-bold">PARCELAS</span>
        {props.parcelas.map((parcela, index) => (
          <div key={`parcela-group-pay-numero-${index}`}>
            {(index < 2 || expandirParcelas) && renderCamposDaParcela(parcela, index)}
          </div>
        ))}
        <div className="d-flex justify-content-between mt-3">
          {renderBotaoAdicionarParcela()}
          {props.parcelas.length > 2 && renderBotaoVerMaisParcelas()}
        </div>
        <div className="d-flex mt-2">
          {valoresDiferentes(props.valorTotalInicial, props.valorTotalParcelas) && (
            <div className="text-danger">
              <span className="fw-bold">Diferença:</span>
              {` R$ ${formatarValorMonetario(props.valorTotalInicial - props.valorTotalParcelas)}`}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCamposDaParcela = (parcela: ValoresDespesaParceladaResponse, index: number) => {
    const isPrimeiraParcela = index === 0;
    const formaPagamento = props.watch(`parcelas.${index}.formaPagamento`);
    const formaPagamentoPix = props.watch(`parcelas.${index}.tipoFormaPagamentoPix`);

    return (
      <>
        <Row key={parcela.id} className="mt-3 align-items-center">
          <Col xs={6} md={3} className="d-flex align-items-end gap-3">
            <span className="fw-bold mb-2">{index + 1}.</span>
            <div style={{ width: "100%" }}>
              <MesCompetencia
                name={`parcelas.${index}.mesCompetencia`}
                label="Mês competência *"
                control={props.control}
                register={props.register}
                setValue={props.setValue}
                defaultValue={parcela.mesCompetencia ? criarDataByLocalDate(parcela.mesCompetencia.toString()) : null}
                style={{ width: "100%" }}
                usarValidacaoParaFormulario
              />
            </div>
          </Col>
          <Col xs={6} md={3}>
            <CustomDatePicker
              name={`parcelas.${index}.dataVencimento`}
              label="Data de vencimento *"
              control={props.control}
              register={props.register}
              setValue={props.setValue}
              defaultValue={parcela.dataVencimento ? criarDataByLocalDate(parcela.dataVencimento.toString()) : null}
              style={{ width: "100%" }}
              usarValidacaoParaFormulario
            />
          </Col>
          <Col xs={6} md={3}>
            <CustomNumberInput
              id={`valor-parcelado-${index}`}
              name={`parcelas.${index}.valorParcela`}
              label="Valor parcelado (R$) *"
              prefix="R$ "
              control={props.control}
              register={props.register}
              setValue={props.setValue}
              fixedDecimalScale={true}
              decimalScale={2}
              defaultValue={parcela.valorParcela}
              usarValidacaoParaFormulario
              isAllowed={(valor) => {
                const { floatValue } = valor;
                return floatValue === undefined || (floatValue >= 0 && floatValue <= 999999999999.99);
              }}
            />
          </Col>
          <Col xs={6} md={3}>
            <CustomNumberInput
              id={`valor-liquido-${index}`}
              name={`parcelas.${index}.valorLiquido`}
              label="Valor líquido (R$) *"
              prefix="R$ "
              control={props.control}
              setValue={props.setValue}
              register={props.register}
              decimalScale={2}
              fixedDecimalScale={true}
              defaultValue={parcela.valorLiquido}
              usarValidacaoParaFormulario
            />
          </Col>
        </Row>

        {
          <>
            <Row className="mt-3">
              <CamposPagamentoParcela index={index} condominioId={props.condominioId} />
              {!isPrimeiraParcela && (
                <Col xs={1} className="mt-4">
                  <CirculoSinalNegativo onClick={() => props.removerParcela(index)} size={16} />
                </Col>
              )}
            </Row>

            {formaPagamento?.value === "PIX" && (
              <Row className="mt-4 align-items-end">
                <Col xs={4}>
                  <Form.Group>
                    <Form.Label>Forma de Pagamento PIX</Form.Label>
                    <div className="d-flex pt-2">
                      <Form.Check
                        inline
                        label="Transferência"
                        type="radio"
                        value="TRANSFERENCIA"
                        {...props.register(`parcelas.${index}.tipoFormaPagamentoPix`)}
                      />
                      <Form.Check
                        inline
                        label="QR Code"
                        type="radio"
                        value="QR_CODE"
                        {...props.register(`parcelas.${index}.tipoFormaPagamentoPix`)}
                      />
                    </div>
                  </Form.Group>
                </Col>
                {formaPagamentoPix === "QR_CODE" && (
                  <Col xs={6}>
                    <TextInput
                      usarValidacaoParaFormulario={true}
                      id={`codigoCopiaColaPix-${index}`}
                      label="Código Copia e Cola PIX"
                      name={`parcelas.${index}.codigoCopiaColaPix`}
                      control={props.control}
                      setValue={props.setValue}
                      register={props.register}
                    />
                  </Col>
                )}
              </Row>
            )}
            <Row className="mt-3 align-items-end">
              <Col xs={8}>
                <TextInput
                  id={`codigo-barras-${index}`}
                  register={props.register}
                  setValue={props.setValue}
                  label={informarManualmente[index] ? "Linha digitável" : "Código de barras"}
                  name={`parcelas.${index}.codigoDeBarras`}
                  placeholder={
                    informarManualmente[index]
                      ? "Informe a linha digitável"
                      : "Aguardando a leitura do código de barras"
                  }
                  readOnly={!informarManualmente[index]}
                  control={props.control}
                  usarValidacaoParaFormulario
                />
              </Col>
              <Col xs={4} className="pb-1">
                <Form.Check
                  type="checkbox"
                  label="Informar manualmente"
                  checked={!!informarManualmente[index]}
                  onChange={(e) => setInformarManualmente((prev) => ({ ...prev, [index]: e.target.checked }))}
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <CustomSwitch
                labelDireito="Anexo"
                usarValidacaoParaFormulario={false}
                checked={parcelaUiState[index]?.showAttachment || false}
                onChange={(e) => handleParcelaUiStateChange(index, "showAttachment", e.target.checked)}
              />
              {parcelaUiState[index]?.showAttachment && (
                <Col xs={4}>
                  <InputAnexo
                    id={`parcela-${index}-anexos`}
                    name={`parcelas.${index}.anexos`}
                    accept=".pdf, .png, .jpg, .jpeg"
                    control={props.control}
                    setValue={props.setValue}
                    hasInfoAdicional={true}
                  />
                </Col>
              )}
            </Row>
          </>
        }
        <hr className="mt-3" />
      </>
    );
  };

  const renderBotaoAdicionarParcela = () => (
    <div
      className="p-2 botao-adicionar-item-lista d-inline-block"
      onClick={() => {
        props.adicionarParcela(criarParcelaVazia());
        if (!expandirParcelas) setExpandirParcelas(true);
      }}
    >
      <FaPlus size={15} /> Adicionar parcela
    </div>
  );

  const renderBotaoVerMaisParcelas = () => (
    <span
      className="p-2 botao-adicionar-item-lista d-inline-block"
      onClick={() => setExpandirParcelas(!expandirParcelas)}
    >
      {expandirParcelas ? "Ver menos" : "Ver mais"}
      {expandirParcelas ? <FaAnglesUp size={15} className="ms-1" /> : <FaAnglesDown size={15} className="ms-1" />}
    </span>
  );

  const renderFornecedorEFavorecido = () => {
    const isFavorecidoFornecedor = props.watch("isFavorecidoFornecedor");

    return (
      <>
        <Row className="mt-4 align-items-end">
          <Col xs={12} md={8}>
            <SelectAsync
              id="fornecedor-despesa-parcelada"
              label={isFavorecidoFornecedor ? "Fornecedor *" : "Fornecedor/Favorecido *"}
              name="fornecedor"
              control={props.control}
              register={props.register}
              setValue={props.setValue}
              onChange={handleFornecedorChange}
              endpoint={`/api/fornecedor/buscar-registros-select-async?condominioId=${props.condominioId}`}
              nomeAtributoLabel="nome"
              nomeAtributoValue="value"
              usarValidacaoParaFormulario
              isClearable={true}
            />
          </Col>

          <Col xs={12} md={4} className="mb-3">
            <Controller
              name="isFavorecidoFornecedor"
              control={props.control}
              render={({ field }) => (
                <Form.Group controlId="isFavorecidoFornecedorCheckbox">
                  <Form.Check
                    type="checkbox"
                    label="Favorecido não é o fornecedor"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </Form.Group>
              )}
            />
          </Col>
        </Row>

        {isFavorecidoFornecedor && (
          <Row className="mt-4">
            <Col xs={8}>
              <SelectAsync
                id="favorecido-despesa-parcelada"
                label="Favorecido *"
                name="favorecido"
                nomeAtributoLabel="nome"
                nomeAtributoValue="value"
                control={props.control}
                endpoint={`/api/fornecedor/buscar-registros-select-async?condominioId=${props.condominioId}`}
                usarValidacaoParaFormulario
                register={props.register}
                setValue={props.setValue}
                isClearable={true}
              />
            </Col>
          </Row>
        )}
      </>
    );
  };

  const renderClassesDeConta = () => (
    <div className="mt-4">
      {props.classesConta.map((_, index) => {
        const sizeDescricao = props.possuiContaProvisionamento ? 3 : 6;
        return (
          <Row key={`classeConta-${index}`} className="mt-3 align-items-center">
            <Col xs={12} md={3}>
              <SelectAsync
                id={`classe-conta-${index}`}
                register={props.register}
                setValue={props.setValue}
                nomeAtributoLabel="descricao"
                nomeAtributoValue="value"
                label="Classe de conta (Categoria) *"
                endpoint={`/api/plano-contas/classe-plano/buscar-registros-select-async?condominioId=${props.condominioId}&tipoGrupo=2`}
                usarValidacaoParaFormulario
                control={props.control}
                name={`classesConta.${index}.classePlano`}
                isClearable={true}
                onChange={(opcaoSelecionada) => handleClasseContaChange(opcaoSelecionada, index)}
              />
            </Col>
            <Col xs={6} md={2}>
              <CustomNumberInput
                id={`valor-bruto-${index}`}
                register={props.register}
                setValue={props.setValue}
                label="Valor bruto (R$) *"
                prefix="R$ "
                decimalScale={2}
                usarValidacaoParaFormulario
                control={props.control}
                name={`classesConta.${index}.valorBruto`}
                isAllowed={(values) => {
                  const { floatValue } = values;
                  return void 0 === floatValue || (floatValue > 0 && floatValue <= 999999999999.99);
                }}
              />
            </Col>
            <Col xs={6} md={sizeDescricao}>
              <TextInput
                id={`descricao-${index}`}
                register={props.register}
                setValue={props.setValue}
                label="Descrição"
                control={props.control}
                name={`classesConta.${index}.descricao`}
                usarValidacaoParaFormulario
              />
            </Col>
            {props.possuiContaProvisionamento && (
              <Col xs={12} md={3}>
                <SelectAsync
                  id={`conta-provisionamento-${index}`}
                  register={props.register}
                  setValue={props.setValue}
                  nomeAtributoLabel="nomeConta"
                  nomeAtributoValue="id"
                  label="Conta de provisionamento *"
                  endpoint={`/api/conta-provisionamento/buscar-registros-select-async-cadastro-despesa/${props.condominioId}`}
                  usarValidacaoParaFormulario
                  name={`classesConta.${index}.contaProvisionamento`}
                  control={props.control}
                  isClearable={true}
                />
              </Col>
            )}
            {index !== 0 && (
              <Col xs={1} className="mt-4">
                <CirculoSinalNegativo onClick={() => props.removerClasseConta(index)} size={16} />
              </Col>
            )}
          </Row>
        );
      })}
      <div className="d-flex align-items-center mt-3">
        <div onClick={adicionarClasseContaVazia} className="botao-adicionar-item-lista">
          <FaPlus size={15} />
          <span className="ms-2">Adicionar classe de conta</span>
        </div>
        {valoresDiferentes(props.valorTotalClassesConta, props.valorTotalInicial) && (
          <div className="text-danger ms-5">
            <span className="fw-bold">Diferença:</span>
            {` R$ ${formatarValorMonetario(props.valorTotalInicial - props.valorTotalClassesConta)}`}
          </div>
        )}
      </div>
    </div>
  );

  const adicionarClasseContaVazia = () => {
    const adicionar = () => {
      props.setValue("retencoes", []);
      props.adicionarClasseConta({
        classePlano: { label: "", value: undefined },
        contaProvisionamento: null,
        valorBruto: undefined,
        condominioPossuiContaProvisionamentoAtiva: props.possuiContaProvisionamento,
      });
    };

    if (props.classesConta.length === 1) {
      showAlert({
        texto: "Ao adicionar mais de uma classe de conta, não será possível informar retenção. Deseja continuar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: adicionar,
      });
    } else {
      adicionar();
    }
  };

  const renderCamposFinais = () => (
    <Row className="mt-4">
      <Col xs={6} md={3}>
        <TextInput
          id="numero-documento-despesa-parcelada"
          register={props.register}
          setValue={props.setValue}
          label="Número do documento"
          name="numeroDocumento"
          control={props.control}
          usarValidacaoParaFormulario
        />
      </Col>
      <Col xs={6} md={3}>
        <TextInput
          id="serie-nf-despesa-parcelada"
          register={props.register}
          setValue={props.setValue}
          label="Série da NF"
          name="serieNf"
          control={props.control}
          usarValidacaoParaFormulario
        />
      </Col>
      <Col xs={12} md={3}>
        <CustomDatePicker
          label="Data de emissão"
          name="dataEmissao"
          value={props.watch(`dataEmissao`) ?? undefined}
          control={props.control}
          register={props.register}
          setValue={props.setValue}
          style={{ width: "100%" }}
          usarValidacaoParaFormulario
        />
      </Col>
    </Row>
  );

  return (
    <div>
      <Row>
        <Col xs={8}>
          <SelectAsync
            usarValidacaoParaFormulario={true}
            id="condominio"
            name="condominio"
            label="Condomínio"
            endpoint="/api/condominio/registros"
            nomeAtributoValue="id"
            nomeAtributoLabel="nomeFantasia"
            control={props.control}
            register={props.register}
            setValue={props.setValue}
            isDisabled
          />
        </Col>
      </Row>
      <div className="mt-4" />
      {renderCampoValorTotal()}
      <div className="mt-4" />
      {renderParcelas()}
      {renderFornecedorEFavorecido()}
      {renderClassesDeConta()}
      {renderCamposFinais()}
    </div>
  );
};

export default SecaoDadosDespesaModalCadastrarDespesaParceladaGroupPay;
